/* eslint-disable */
import type { CardPresentationStyle } from "./CardPresentationStyleProto.pb";

export interface CardTheme {
  /** Opaque token used to identify this card theme. */
  token?: string;
  /**
   * Title describing the card customization.
   *
   * @deprecated
   */
  title?: string;
  /**
   * Image showing the front of a blank card with this theme without signature.
   *
   * @deprecated
   */
  card_image_url?: string;
  /**
   * Image showing the texture of the card. Used for the theme selection buttons.
   *
   * @deprecated
   */
  selector_image_url?: string;
  /**
   * Color of the indicator on the selection button, in RGB hex format.
   *
   * @deprecated
   */
  selector_foreground_color?: string;
  /** Color of laser-etched customization, in RGB hex format. */
  ink_color?: string;
  /**
   * Predominant color of the card, in RGB hex format (e.g. "#000000"). This
   * will be used for the physical card preview when the background image is
   * null, and should always be used for the background of the card info view.
   */
  card_color?: string;
  /**
   * Predominant highlight color of the card, in RGB hex format (e.g. "#000000").
   * The card will change to this color when a user presses on it in the card drawer.
   */
  card_highlight_color?: string;
  /**
   * Color of the border around the selection button, in RGB hex format (e.g. "#000000").
   *
   * @deprecated
   */
  selector_border_color?: string;
  border_color?: string;
  /** @deprecated */
  id?: CardThemeIdentifier;
  /** Gradient style to apply on top of the card background color. */
  gradient_style?: CardThemeGradient;
  /**
   * Background image of the card. When non-null, this image should be preferred
   * over using the card color.
   */
  background_image?: CardThemeBackgroundImage;
  /** TODO: move out of CardTheme into something specific to Card Tab metadata */
  card_info_text_color?: string;
  /** Color of the mag stripe on the 3D card, in RGB hex format (e.g. "#000000"). */
  mag_stripe_color?: string;
  /** Color of laser-etched customization on the back of the card, in RGB hex format. */
  back_ink_color?: string;
  /** Color of the help link on the back of the card, in RGB hex format */
  help_link_color?: string;
  card_customization_margin?: CardThemeCardCustomizationMargin;
  /** Which set of card themes this one belongs to - eg. Classic, Limited Edition, Metal. */
  group_name?: string;
  /** Which font to show on the text of the card. */
  font?: CardThemeFont;
}

export enum CardThemeIdentifier {
  BLACK = "BLACK",
  WHITE = "WHITE",
}

export enum CardThemeGradient {
  DIAGONAL_DARK = "DIAGONAL_DARK",
  DIAGONAL_LIGHT = "DIAGONAL_LIGHT",
}

export enum CardThemeBackgroundImage {
  HOOD_BY_AIR = "HOOD_BY_AIR",
  HUNDRED_THIEVES = "HUNDRED_THIEVES",
  METAL_OIL_SLICK = "METAL_OIL_SLICK",
  METAL_BLACK = "METAL_BLACK",
  SHANTELL_MARTIN = "SHANTELL_MARTIN",
  SLIMEGUY = "SLIMEGUY",
  TAP_DEVICE_TRIAL = "TAP_DEVICE_TRIAL",
  DREAM = "DREAM",
  MOOD = "MOOD",
}

export enum CardThemeFont {
  CASH_MARKET_LIGHT = "CASH_MARKET_LIGHT",
  SHANTELL_SANS = "SHANTELL_SANS",
  CASH_MARKET_REGULAR = "CASH_MARKET_REGULAR",
}

export interface CardThemeCardCustomizationMargin {
  /** The color of the margins in RGB hex format (#000000) * */
  color?: string;
  /** The alpha value applied to color (0.30) * */
  alpha?: number;
  /**
   * The uniform size the margins will protect, as a percentage of the card width
   * (0.03)
   */
  size?: number;
}

/**
 * Represents theme properties specific to the card customization screen - where the customer
 * decides whether to customize the card with a signature, some stamps, or a cashtag.
 */
export interface CardCustomizationControlsTheme {
  empty_customization_outline_color?: string;
  filled_customization_outline_color?: string;
  controls_tint_color?: string;
}

/**
 * Represents the touch data - where and when the customer touched the client's screen to create
 * an image. Currently used to capture information for a customer's signature.
 */
export interface TouchData {
  width?: number;
  height?: number;
  strokes?: TouchDataStroke[];
  stamps?: TouchDataStampCustomization[];
}

export interface TouchDataPoint {
  /**
   * Coordinates are relative to (0,0), the top left corner of the expected signature area, and
   * can fall outside of the boundary described by height and width.
   */
  x_coordinate?: number;
  y_coordinate?: number;
  /**
   * Time in milliseconds between when the first point in the touch data and this point were
   * drawn.
   */
  created_at?: number;
}

export interface TouchDataStroke {
  points?: TouchDataPoint[];
}

export interface TouchDataStampCustomization {
  /** The identifier of the stamp from the Stamps config. */
  identifier?: string;
  /** The coordinates of the stamp's center point, relative to the top-left corner. */
  center?: TouchDataPoint;
  /** The stamp's size before rotation. */
  width?: number;
  height?: number;
  /** The stamp's rotation in radians, clockwise from original. */
  rotation?: number;
}

export interface CardCustomizationData {
  card_theme?: CardTheme;
  touch_data?: TouchData;
  show_cashtag?: boolean;
  /** An indicator for identifying whether it is the legacy or full-face customization. */
  customization_area?: CardCustomizationDataCustomizationArea;
  /**
   * Identifier for presenting the card in 3D.
   *
   * @deprecated
   */
  is_2d?: boolean;
  /** Identifier for which presentation style should be used. */
  card_presentation_style?: CardPresentationStyle;
  /** formatted cashtag including currency symbol (e.g., $, £) to show on user's Cash Cards */
  formatted_cashtag?: string;
}

export enum CardCustomizationDataCustomizationArea {
  LEGACY = "LEGACY",
  FULL_FACE = "FULL_FACE",
}

export interface CardCustomization {
  customization_token?: string;
  customer_token?: string;
  trunk_token?: string;
  card_customization_data?: CardCustomizationData;
}
